import type { HTMLAttributes, MouseEvent, ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { Button, Popover, cn } from '@tractian/ui'
import { CaretDownIcon, CaretUpIcon, FilterIcon, SettingsIcon } from '@tractian/icons'
import { Checkbox } from '@/components/primitives/checkbox'
import type { ColDef, ColGroupDef, IHeaderParams, SortDirection } from 'ag-grid-community'
import { themeQuartz } from 'ag-grid-community'
import type { AgGridReactProps } from 'ag-grid-react'
import { AgGridReact } from 'ag-grid-react'

export const agTheme = themeQuartz.withParams({
  backgroundColor: '#ffffff',
  headerBackgroundColor: '#ffffff',
  headerTextColor: '#24292F',
  headerFontWeight: 500,
  columnBorder: true,
  headerColumnBorder: true,
  headerColumnBorderHeight: '100%',
  headerColumnResizeHandleColor: 'transparent',
  wrapperBorder: false,
  pinnedColumnBorder: false,
  rowHoverColor: '#f9fafb',
  selectedRowBackgroundColor: '#eff6ff',
  borderColor: '#e5e7eb',
  fontSize: 13,
  fontFamily: 'inherit',
  rowHeight: 36,
  headerHeight: 34,
  oddRowBackgroundColor: '#fcfcfc',
  rangeSelectionBorderColor: '#2563eb',
  iconButtonHoverColor: '#9ca3af'
})

function CustomSortHeader(props: IHeaderParams) {
  const colId = props.column.getColId()
  const hasFilter = Boolean(props.column.getColDef().filter)
  const [sortState, setSortState] = useState<SortDirection | null | undefined>(
    props.column.getSort()
  )
  const [isFilterActive, setIsFilterActive] = useState(() => props.column.isFilterActive())

  useEffect(() => {
    const handler = () => setSortState(props.column.getSort())
    props.column.addEventListener('sortChanged', handler)
    return () => props.column.removeEventListener('sortChanged', handler)
  }, [props.column])

  useEffect(() => {
    if (!hasFilter) return
    const handler = () => setIsFilterActive(props.column.isFilterActive())
    props.column.addEventListener('filterActiveChanged', handler)
    return () => props.column.removeEventListener('filterActiveChanged', handler)
  }, [hasFilter, props.column])

  const isSortable = props.column.isSortable()

  function handleFilterClick(event: MouseEvent<HTMLElement>) {
    event.stopPropagation()
    event.preventDefault()
    props.api.showColumnFilter(colId)
  }

  return (
    <div
      className={cn(
        'group flex w-full items-center gap-1 overflow-hidden',
        isSortable && 'cursor-pointer'
      )}
      onClick={isSortable ? () => props.progressSort() : undefined}
    >
      <span className="truncate">{props.displayName}</span>
      {isSortable && (
        <span className="ml-auto flex flex-shrink-0 flex-col items-center" style={{ gap: 0 }}>
          <CaretUpIcon
            className={cn('h-2.5 w-2.5', sortState === 'asc' ? 'text-blue-500' : 'text-slate-300')}
          />
          <CaretDownIcon
            className={cn(
              '-mt-0.5 h-2.5 w-2.5',
              sortState === 'desc' ? 'text-blue-500' : 'text-slate-300'
            )}
          />
        </span>
      )}
      {hasFilter && (
        <Button
          variant="link"
          size="small"
          aria-label={`Filter ${props.displayName}`}
          data-testid={`column-filter-${colId}`}
          prefixIcon={<FilterIcon className="h-3.5 w-3.5" />}
          onClick={handleFilterClick}
          className={cn(
            'px-0 text-slate-300 group-hover:text-gray-500 hover:text-gray-700 focus:text-gray-700',
            isSortable ? 'ml-1' : 'ml-auto',
            isFilterActive && 'text-blue-500 group-hover:text-blue-500 hover:text-blue-500'
          )}
        />
      )}
    </div>
  )
}

export type SkeletonShape = 'text' | 'avatar' | 'icon' | 'avatar-text' | 'icon-text' | 'none'

function SkeletonCell({ shape = 'text' }: { shape?: SkeletonShape }) {
  if (shape === 'none') return null
  if (shape === 'avatar')
    return (
      <div className="flex h-full items-center px-4">
        <div className="h-6 w-6 animate-pulse rounded-full bg-gray-200" />
      </div>
    )
  if (shape === 'icon')
    return (
      <div className="flex h-full items-center px-4">
        <div className="h-5 w-5 animate-pulse rounded bg-gray-200" />
      </div>
    )
  if (shape === 'avatar-text')
    return (
      <div className="flex h-full items-center gap-2 px-4">
        <div className="h-6 w-6 flex-shrink-0 animate-pulse rounded-full bg-gray-200" />
        <div className="h-3.5 w-28 animate-pulse rounded bg-gray-200" />
      </div>
    )
  if (shape === 'icon-text')
    return (
      <div className="flex h-full items-center gap-2 px-4">
        <div className="h-5 w-5 flex-shrink-0 animate-pulse rounded bg-gray-200" />
        <div className="h-3.5 w-24 animate-pulse rounded bg-gray-200" />
      </div>
    )
  return <div className="h-4 w-full animate-pulse rounded bg-gray-200" />
}

function withSkeletonRenderer(colDef: ColDef, shape: SkeletonShape = 'text'): ColDef {
  const { cellRendererParams: _dropped, ...rest } = colDef as ColDef & {
    cellRendererParams?: unknown
  }
  return {
    ...rest,
    sortable: false,
    editable: false,
    cellRenderer: () => <SkeletonCell shape={shape} />
  }
}

type SkeletonProps<T = unknown> = {
  columnDefs: (ColDef<T> | ColGroupDef<T>)[] | null | undefined
  shapes?: SkeletonShape[]
  rowCount?: number
  rowHeight?: number
  headerHeight?: number
  className?: string
}

function Skeleton<T = unknown>({
  columnDefs,
  shapes,
  rowCount = 10,
  rowHeight = 36,
  headerHeight = 34,
  className
}: SkeletonProps<T>) {
  const skeletonColDefs = columnDefs?.map((cd, i) =>
    withSkeletonRenderer(cd as ColDef, shapes?.[i] ?? 'text')
  )
  const rowData = Array.from({ length: rowCount }, (_, i) => ({ id: `sk-${i}` }))

  return (
    <div className={cn('h-full w-full bg-white', className)}>
      <AgGridReact
        theme={agTheme}
        rowData={rowData}
        columnDefs={skeletonColDefs}
        rowHeight={rowHeight}
        headerHeight={headerHeight}
        domLayout="autoHeight"
        suppressRowHoverHighlight
        suppressCellFocus
        suppressRowClickSelection
        suppressLoadingOverlay
        defaultColDef={{ headerComponent: CustomSortHeader }}
      />
    </div>
  )
}

function Grid<T = unknown>({
  className,
  defaultColDef,
  ...props
}: AgGridReactProps<T> & { className?: string }) {
  const mergedDefaultColDef = { headerComponent: CustomSortHeader, ...defaultColDef }
  return (
    <AgGridReact
      theme={agTheme}
      className={className}
      defaultColDef={mergedDefaultColDef}
      suppressNoRowsOverlay
      suppressLoadingOverlay
      {...props}
    />
  )
}

function Root({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'relative grid h-full w-full overflow-hidden',
        "[grid-template-areas:'content''footer']",
        '[grid-template-columns:1fr] [grid-template-rows:1fr_auto]',
        className
      )}
      {...props}
    />
  )
}

function Content({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('relative p-0 [grid-area:content]', className)} {...props} />
}

function Footer({ className, ...props }: HTMLAttributes<HTMLElement>) {
  return (
    <footer
      className={cn(
        'relative flex h-9 max-h-9 w-full items-center justify-end gap-2.5 border-t border-gray-200 bg-white px-3',
        '[grid-area:footer]',
        className
      )}
      {...props}
    />
  )
}

function Empty({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-[2px]',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

type ToggleableColumn = {
  field: string
  label: string
}

type ColumnToggleProps = {
  /** Stable identity for the table's column-visibility state; used as an aria hook, not for I/O. */
  storageKey: string
  columns: ToggleableColumn[]
  hiddenCols: string[]
  onChange: (hidden: string[]) => void
  triggerIcon?: ReactNode
  restoreLabel?: string
}

function ColumnToggle({
  storageKey,
  columns,
  hiddenCols,
  onChange,
  triggerIcon,
  restoreLabel = 'Restore defaults'
}: ColumnToggleProps) {
  const [open, setOpen] = useState(false)

  function toggleCol(field: string) {
    onChange(
      hiddenCols.includes(field) ? hiddenCols.filter(f => f !== field) : [...hiddenCols, field]
    )
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button
          variant="link"
          size="small"
          aria-label={`Toggle column visibility for ${storageKey}`}
          prefixIcon={triggerIcon ?? <SettingsIcon className="h-4 w-4" />}
        />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="end" className="w-44 p-1.5">
          <p className="mb-1 px-2 pt-0.5 text-[10px] font-semibold uppercase tracking-widest text-gray-400">
            Columns
          </p>
          {columns.map(col => {
            const visible = !hiddenCols.includes(col.field)
            return (
              <label
                key={col.field}
                className="flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
              >
                <Checkbox
                  size="small"
                  checked={visible}
                  onCheckedChange={() => toggleCol(col.field)}
                  aria-label={col.label}
                />
                {col.label}
              </label>
            )
          })}
          <div className="mt-1 border-t border-gray-100 pt-1">
            <button
              className="flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-left text-xs text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600"
              onClick={() => onChange([])}
            >
              {restoreLabel}
            </button>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

export const AgTable = {
  Root,
  Content,
  Footer,
  Empty,
  Skeleton,
  Grid,
  ColumnToggle
} satisfies Record<string, unknown>

/** @deprecated Use AgTable compound components instead */
type LegacyAgTableProps = AgGridReactProps & {
  wrapperClassName?: string
  gridClassName?: string
  footer?: ReactNode
}

/** @deprecated Use AgTable compound components instead */
export function LegacyAgTable({
  wrapperClassName,
  gridClassName: _gc,
  footer,
  ...props
}: LegacyAgTableProps) {
  return (
    <AgTable.Root className={wrapperClassName}>
      <AgTable.Grid {...props} />
      {footer && <AgTable.Footer>{footer}</AgTable.Footer>}
    </AgTable.Root>
  )
}
